import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ImageOff, X } from 'lucide-react';

import type { PictureItem } from '../../services/types';

interface PartPicturePanelProps {
  pictures: PictureItem[];
  loading: boolean;
}

export const PartPicturePanel: React.FC<PartPicturePanelProps> = ({
  pictures,
  loading,
}) => {
  const { t } = useTranslation();
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const hasPictures = pictures.length > 0;

  // Keyboard navigation
  useEffect(() => {
    if (!carouselOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setActiveIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight')
        setActiveIndex((i) => Math.min(pictures.length - 1, i + 1));
      if (e.key === 'Escape') setCarouselOpen(false);
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [carouselOpen, pictures.length]);

  const openCarousel = (index = 0) => {
    setActiveIndex(index);
    setCarouselOpen(true);
  };

  // ── Thumbnail square ──────────────────────────────────────────────────────
  const thumbContent = () => {
    if (loading) {
      return (
        <div className="w-32 h-32 bg-gray-100 rounded border border-gray-200 animate-pulse flex-shrink-0" />
      );
    }

    if (!hasPictures) {
      return (
        <div className="w-32 h-32 bg-gray-100 rounded border border-gray-200 flex-shrink-0 flex flex-col items-center justify-center gap-1">
          <ImageOff className="w-7 h-7 text-gray-300" />
          <span className="text-xs text-gray-400 text-center leading-tight px-1">
            {t('parts.pictures.noPhoto')}
          </span>
        </div>
      );
    }

    return (
      <button
        onClick={() => openCarousel(0)}
        className="w-32 h-32 rounded border border-gray-200 overflow-hidden flex-shrink-0 group relative focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={t('parts.pictures.viewPhoto')}
      >
        <img
          src={pictures[0].thumbnailUrl}
          alt={`${pictures[0].partNumber} #1`}
          className="w-full h-full object-cover"
        />
        {pictures.length > 1 && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end justify-end p-1">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 text-white text-xs rounded px-1.5 py-0.5">
              +{pictures.length - 1}
            </span>
          </div>
        )}
      </button>
    );
  };

  // ── Carousel modal ────────────────────────────────────────────────────────
  return (
    <>
      {thumbContent()}

      {carouselOpen && hasPictures && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex flex-col items-center justify-center"
          onClick={() => setCarouselOpen(false)}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/20 transition-colors"
            onClick={() => setCarouselOpen(false)}
            aria-label={t('common.close')}
          >
            <X className="w-6 h-6" />
          </button>

          {/* Main image + arrows */}
          <div
            className="flex items-center gap-4 px-4 max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Prev arrow */}
            <button
              onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
              disabled={activeIndex === 0}
              className="p-2 rounded-full bg-white/10 hover:bg-white/25 disabled:opacity-20 disabled:cursor-not-allowed transition-colors text-white flex-shrink-0"
              aria-label={t('common.previous')}
            >
              <ChevronLeft className="w-7 h-7" />
            </button>

            {/* Image */}
            <div className="flex-1 flex justify-center max-h-[60vh]">
              <img
                src={pictures[activeIndex].fullUrl}
                alt={`${pictures[activeIndex].partNumber} #${activeIndex + 1}`}
                className="max-h-[60vh] max-w-full object-contain rounded shadow-lg"
              />
            </div>

            {/* Next arrow */}
            <button
              onClick={() =>
                setActiveIndex((i) => Math.min(pictures.length - 1, i + 1))
              }
              disabled={activeIndex === pictures.length - 1}
              className="p-2 rounded-full bg-white/10 hover:bg-white/25 disabled:opacity-20 disabled:cursor-not-allowed transition-colors text-white flex-shrink-0"
              aria-label={t('common.next')}
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          </div>

          {/* Counter */}
          <p
            className="text-white/70 text-sm mt-3"
            onClick={(e) => e.stopPropagation()}
          >
            {t('parts.pictures.counter', {
              current: activeIndex + 1,
              total: pictures.length,
            })}
          </p>

          {/* Thumbnail strip */}
          <div
            className="flex gap-2 mt-4 px-4 overflow-x-auto max-w-full pb-1"
            onClick={(e) => e.stopPropagation()}
          >
            {pictures.map((pic, i) => (
              <button
                key={`${pic.partNumber}-${pic.index}`}
                onClick={() => setActiveIndex(i)}
                className={`w-14 h-14 rounded overflow-hidden flex-shrink-0 border-2 transition-colors focus:outline-none ${
                  i === activeIndex
                    ? 'border-blue-400'
                    : 'border-transparent opacity-60 hover:opacity-90'
                }`}
                aria-label={t('parts.pictures.counter', {
                  current: i + 1,
                  total: pictures.length,
                })}
              >
                <img
                  src={pic.thumbnailUrl}
                  alt={`${pic.partNumber} #${i + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
